import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowUpRight, FlaskConical, ScanLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Container, Eyebrow, H2, H3, Lede, Ordinal, Section, SectionHeader, ButtonLink,
} from "../components/Primitives";
import { ProductGrid, ProductGridSkeleton } from "../components/ProductCard";
import { HeroTrust } from "../components/TrustStrip";
import { introDelay } from "../components/Intro";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { MukhiQuickNav } from "../components/QuickNav";
import { RashiCarousel } from "../components/RashiCarousel";
import { GrahaCarousel } from "../components/GrahaCarousel";
import { ProcessRail } from "../components/ProcessRail";
import { VideoBand } from "../components/VideoBand";
import { Ornament } from "../components/Ornament";
import { WhyUs } from "../components/WhyUs";
import { Testimonials } from "../components/Testimonials";
import { FinalCTA } from "../components/FinalCTA";
import { IMAGES } from "../content/images";
import { TOOLS_NAV } from "../content/nav";
import {
  getFestivals, getProducts, getScoreboard, getService, joinCommunity, apiError,
} from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useSEO, orgSchema } from "../lib/seo";
import {
  CountUp, EASE_OUT, LineReveal, Reveal, Stagger, StaggerItem, motion,
} from "../lib/motion";

/** 90px desktop / 60px mobile rhythm, overriding the default Section padding. */
const PAD = "!py-[90px] max-md:!py-[60px]";

/** Shared ambient background applied to the Graha + Rashi bands. Kept as an
 *  inline style because CRA's css-loader can't resolve `/public` paths from
 *  index.css. See the `.ambient-scene` class for the scrim, sizing, and
 *  positioning. */
const AMBIENT_BG_STYLE = {
  backgroundImage: `url("${process.env.PUBLIC_URL || ""}/img/ambient/section-ambient-bg.png")`,
};

const PROMISE_TEASERS = [
  "Your consultant earns nothing from what you buy.",
  "See every stone loose before it's set.",
  "30 days, money back to your card — never store credit.",
  "We'll tell you when you need nothing, and we publish how often.",
];

const PROOF = [
  {
    n: 1, icon: ShieldCheck, image: IMAGES.certificate, span: "lg:col-span-7", ratio: "aspect-[16/10]",
    title: "The report isn't ours",
    body: "Every gemstone above ₹10,000 carries a report from GIA, Gem Testing Laboratory Jaipur or IGI-GTL. Scan the QR and it opens the laboratory's own website. We cannot edit that page — and that is precisely why we point you at it rather than at a certificate of our own.",
    link: { to: "/about/verification", label: "How to verify" },
  },
  {
    n: 2, icon: ScanLine, image: IMAGES.xray, span: "lg:col-span-5", ratio: "aspect-[16/10]",
    title: "Count the chambers yourself",
    body: "Every rudraksha above ₹3,000 is X-rayed, and the actual radiograph is published on its product page. A genuine bead has one seed chamber per mukhi line — grooves carved on the outside cannot fake what is inside.",
    link: { to: "/shop/rudraksha", label: "See an example" },
  },
  {
    n: 3, icon: FlaskConical, image: IMAGES.sourcing, span: "lg:col-span-5", ratio: "aspect-[16/10]",
    title: "Test us, at our expense",
    body: "Take anything you buy to any accredited laboratory within 30 days. If the species, treatment, specific gravity or refractive index differ from our report, you get a full refund and we pay your laboratory fee.",
    link: { to: "/promises", label: "Read the promise" },
  },
];

function ProofCard({ item }) {
  const { icon: Icon, image, title, body, link, n, span, ratio } = item;
  return (
    <StaggerItem className={`group card-s elev flex flex-col overflow-hidden ${span}`}>
      <div className={`overflow-hidden bg-wash ${ratio}`}>
        <motion.img
          src={image}
          alt=""
          loading="lazy"
          className="dim-dark h-full w-full object-cover"
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 1, ease: EASE_OUT }}
        />
      </div>
      <div className="flex flex-1 flex-col p-7 lg:p-8">
        <div className="mb-4 flex items-center gap-4">
          <Ordinal n={n} className="!text-2xl" />
          <span className="h-px flex-1 bg-rule" />
          <Icon className="h-4 w-4 text-verify" aria-hidden="true" />
        </div>
        <H3>{title}</H3>
        <p className="mt-3 flex-1 text-[15px] leading-[1.75] text-dim">{body}</p>
        <Link to={link.to} className="link-u ui mt-6 inline-flex items-center gap-1.5 self-start text-sm">
          {link.label}
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </div>
    </StaggerItem>
  );
}

function ScoreboardStrip() {
  const { data } = useQuery({ queryKey: ["scoreboard"], queryFn: getScoreboard });
  if (!data) return null;
  const c = data.current;
  const items = [
    { key: "refund_rate", value: c.refund_rate, suffix: "%", label: "Refund rate" },
    { key: "median_refund_days", value: c.median_refund_days, suffix: " days", label: "Median refund turnaround" },
    { key: "dispatch_72h_rate", value: c.dispatch_72h_rate, suffix: "%", label: "Dispatched within 72 hours" },
    { key: "no_recommendation_rate", value: c.no_recommendation_rate, suffix: "%", label: "Consultations ending with no product recommendation" },
  ];
  return (
    <Stagger data-testid="scoreboard-strip" className="grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-4" stagger={0.08}>
      {items.map((i) => (
        <StaggerItem key={i.key} className="bg-surface p-8">
          <CountUp
            value={i.value}
            decimals={1}
            suffix={i.suffix}
            className="num block text-4xl text-deep"
            testId={`strip-${i.key}`}
          />
          <p className="ui mt-3 text-xs leading-snug text-dim">{i.label}</p>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

function CommunityForm() {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("We need your explicit consent before adding you.");
      return;
    }
    setBusy(true);
    try {
      const r = await joinCommunity({ phone, consent });
      toast.success(r.message);
      setPhone("");
      setConsent(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md space-y-5" data-testid="community-form">
      <div className="flex gap-2">
        <input
          required
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone number with country code"
          aria-label="Phone number"
          data-testid="community-phone"
          className="field num"
        />
        <button type="submit" disabled={busy} className="btn btn-primary shrink-0" data-testid="community-join">
          {busy ? "Joining…" : "Join"}
        </button>
      </div>
      <label htmlFor="community-consent" className="flex cursor-pointer items-start gap-3">
        <input
          id="community-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="community-consent"
          className="mt-1 h-4 w-4 accent-[var(--c-gold)]"
        />
        <span className="ui text-xs leading-[1.7] text-dim">
          Yes, send me about one WhatsApp message a week. I can leave at any time using the unsubscribe
          link in every message.
        </span>
      </label>
      <p className="ui text-xs text-dim">We will never call you unless you book a call.</p>
    </form>
  );
}

export default function Home() {
  const { format } = useCurrency();
  const [D] = useState(introDelay);

  useSEO({
    title: "Certified Gemstones & Rudraksha Online · Lab-Verified, Flat-Fee Astrology Guidance",
    description:
      "Certified gemstones and rudraksha with reports you verify on the laboratory's own website. Flat-fee astrology and numerology guidance from consultants who earn nothing from what you buy.",
    canonical: "/",
    jsonLd: [orgSchema, {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Shreem Mantra Energy",
      url: "https://shreemmantraenergy.com",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://shreemmantraenergy.com/shop?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    }],
  });

  const featured = useQuery({
    queryKey: ["products", "featured"],
    queryFn: () => getProducts({ type: "gemstone", limit: 8, sort: "price-desc" }),
  });
  const rudraksha = useQuery({
    queryKey: ["products", "rudraksha-home"],
    queryFn: () => getProducts({ type: "rudraksha", limit: 6, sort: "price-asc" }),
  });
  const semiPrecious = useQuery({
    queryKey: ["products", "semi-precious-home"],
    queryFn: () => getProducts({ type: "semi-precious", limit: 8 }),
  });
  const festivals = useQuery({ queryKey: ["festivals"], queryFn: getFestivals });
  const proofFirst = useQuery({
    queryKey: ["service", "suitability-session"],
    queryFn: () => getService("suitability-session"),
  });

  const svc = proofFirst.data?.service;

  return (
    <>
      {/* 1 — Hero: content centred on the film, the trust icons anchored to its foot */}
      <VideoBand
        src="/media/devotional_web.mp4"
        webm="/media/devotional_web.webm"
        poster="/media/devotional_poster.jpg"
        eager
        rate={1}
        scrim={0.4}
        surround="#faf7f1"
        minHeight="100svh"
        testId="hero"
        className="border-b border-rule"
      >
        <div className="flex min-h-[100svh] flex-col">
          <Container className="flex flex-1 flex-col items-center justify-center py-[90px] text-center max-md:py-16">
            {/* Hero simplified per B2 — the śrīṁ mark + ornament act as one
                unit, then H1, subhead, CTAs. Eyebrow and the ₹499 tagline
                were cut; the tagline now lives in the section below. */}
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: EASE_OUT, delay: D }}
            >
              <p className="deva glow-mark text-3xl leading-none text-[#f0d693]">श्रीं</p>
              <Ornament className="mb-2 mt-6" />
            </motion.div>

            <h1 className="hero-h1 hero-h1--blend mx-auto mt-8 max-w-[var(--measure-tight)] !text-[rgba(243,239,231,0.95)]">
              <LineReveal
                delay={D + 0.25}
                stagger={0.14}
                duration={0.95}
                lines={[
                  "Find inner peace",
                  <span className="italic font-normal text-[rgba(240,214,147,0.92)]">and outer purpose.</span>,
                ]}
              />
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: D + 0.65, ease: EASE_OUT }}
            >
              <div className="mt-10 flex flex-wrap justify-center gap-3.5">
                <ButtonLink to="/shop/gemstones" className="btn-cta" data-testid="hero-shop-cta">
                  Find your stone — every one lab-verified
                </ButtonLink>
                <ButtonLink
                  to="/consult/suitability-session"
                  variant="secondary"
                  className="btn-cta"
                  data-testid="hero-consult-cta"
                >
                  Book a session — ₹499
                </ButtonLink>
              </div>
            </motion.div>
          </Container>

          <HeroTrust delay={D + 0.9} />
        </div>
      </VideoBand>

      {/* 3 — Navgraha carousel (Pattachitra illustrations) */}
      <Section
        className={`${PAD} border-b border-rule ambient-scene force-dark`}
        style={AMBIENT_BG_STYLE}
      >
        <Container>
          <GrahaCarousel />
        </Container>
      </Section>

      {/* 4 — How a personal reading works. The ambient scene image + scrim
          holds if the video fails to load (matches sections 3 and 5); the
          silent process-golden-form clip plays on top when available. */}
      <Section
        className={`${PAD} force-dark relative isolate overflow-hidden border-b border-rule`}
        data-testid="process-band"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 z-0"
          style={{
            ...AMBIENT_BG_STYLE,
            backgroundColor: "#100e0a",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <video
          aria-hidden="true"
          data-testid="process-band-video"
          className="absolute inset-0 z-[1] h-full w-full object-cover"
          poster="/video/process-golden-form-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src="/video/process-golden-form.webm" type="video/webm" />
          <source src="/video/process-golden-form.mp4" type="video/mp4" />
        </video>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[2]"
          style={{ backgroundColor: "rgba(20, 17, 13, 0.55)" }}
        />
        <Container className="relative z-10">
          <Reveal className="text-center">
            <Ornament className="mb-6" />
            <p className="eyebrow-wide">How a personal reading works</p>
            <h2 className="section-title mx-auto mt-4 max-w-3xl">
              <LineReveal
                lines={["Four steps, from your chart", "to the stone on your wrist"]}
                inViewAmount={0.4}
                stagger={0.12}
                duration={0.9}
              />
            </h2>
          </Reveal>
          <div className="mt-12">
            <ProcessRail />
          </div>
          <Reveal className="mt-11 flex flex-wrap justify-center gap-3.5">
            <ButtonLink
              to="/consult/suitability-session"
              className="btn-cta"
              data-testid="process-book-cta"
            >
              Book a reading — ₹499
            </ButtonLink>
          </Reveal>
        </Container>
      </Section>

      {/* 5 — Rashi carousel (Pattachitra film strip) */}
      <Section
        className={`${PAD} overflow-hidden border-b border-rule ambient-scene force-dark`}
        style={AMBIENT_BG_STYLE}
      >
        <Container>
          <RashiCarousel />
        </Container>
      </Section>

      {/* 6 — Why us */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container>
          <SectionHeader
            eyebrow="Why us"
            title="Six things you can check, rather than six things we assert"
          />
          <WhyUs />
          <Reveal className="mt-12 text-center">
            <Link to="/promises" className="link-u ui text-sm" data-testid="whyus-promises-cta">
              See the promises in writing →
            </Link>
          </Reveal>
        </Container>
      </Section>

      {/* 7 — Featured stones */}
      <Section className={`${PAD} border-b border-rule`}>
        <Container>
          <SectionHeader
            eyebrow="Featured stones"
            title="Real inventory, live stock, honest prices"
            lede="Every stone is photographed out of its packet, on a grey card, with a millimetre scale in frame. No stock imagery, ever."
            action={
              <Link to="/shop/gemstones" className="link-u ui text-sm" data-testid="see-all-gemstones">
                See all gemstones →
              </Link>
            }
          />
          {featured.isLoading ? (
            <ProductGridSkeleton count={8} testId="featured-skeleton" />
          ) : (featured.data?.items || []).length ? (
            <ProductGrid products={(featured.data?.items || []).slice(0, 8)} testId="featured-grid" />
          ) : (
            <div className="border border-rule p-10 text-center" data-testid="featured-empty">
              <p className="eyebrow-wide">Coming soon</p>
              <p className="mx-auto mt-3 max-w-[var(--measure-read)] text-sm leading-[1.75] text-dim">
                Certified gemstones are being prepared for launch. Each will go live once its
                laboratory report and photograph are in. Message us for anything specific.
              </p>
            </div>
          )}
        </Container>
      </Section>

      {/* 7b — Semi-Precious Collection band (launched 15 Oct 2026) */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container>
          <SectionHeader
            eyebrow="The Semi-Precious Collection"
            title="Malas, bracelets and raw stones — curated in small numbers"
            lede="A separate shelf from the certified gemstones. Each piece is representative — the real photograph is added before it ships."
            action={
              <Link
                to="/collection/semi-precious"
                className="link-u ui text-sm"
                data-testid="see-all-semi-precious"
              >
                See the full collection →
              </Link>
            }
          />
          {semiPrecious.isLoading ? (
            <ProductGridSkeleton count={8} testId="semi-precious-home-skeleton" />
          ) : (semiPrecious.data?.items || []).length ? (
            <ProductGrid
              products={(semiPrecious.data?.items || []).slice(0, 8)}
              testId="semi-precious-home-grid"
            />
          ) : (
            <div className="border border-rule p-10 text-center" data-testid="semi-precious-home-empty">
              <p className="eyebrow-wide">Coming soon</p>
              <p className="mx-auto mt-3 max-w-[var(--measure-read)] text-sm leading-[1.75] text-dim">
                This collection is being prepared. Message us about any piece you're looking for.
              </p>
            </div>
          )}
        </Container>
      </Section>

      {/* 8 — The proof block, as a bento */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container>
          <SectionHeader
            eyebrow="How you can check us"
            title="Three claims, and how to verify each one yourself"
          />
          <Stagger className="grid gap-6 lg:grid-cols-3 lg:grid-rows-2" stagger={0.1}>
            {PROOF.map((p) => <ProofCard key={p.n} item={p} />)}
          </Stagger>
        </Container>
      </Section>

      {/* 9 — Rudraksha */}
      <Section className={`${PAD} border-b border-rule`}>
        <Container>
          <SectionHeader
            eyebrow="Rudraksha"
            title="Nepal and Java rudraksha, X-rayed and priced by mukhi — never by size"
            lede="A well-chosen 15mm bead is not spiritually inferior to a 30mm one. We price by mukhi and quality, and we will tell you when a cheaper bead is the right bead."
          />
          {rudraksha.isLoading ? (
            <ProductGridSkeleton count={4} testId="rudraksha-skeleton" />
          ) : (rudraksha.data?.items || []).length ? (
            <ProductGrid products={rudraksha.data?.items || []} testId="rudraksha-grid" />
          ) : (
            <div className="border border-rule p-10 text-center" data-testid="rudraksha-empty">
              <p className="eyebrow-wide">Coming soon</p>
              <p className="mx-auto mt-3 max-w-[var(--measure-read)] text-sm leading-[1.75] text-dim">
                Rudraksha beads are being X-rayed and prepared for launch. Each will go live with
                its own radiograph.
              </p>
            </div>
          )}

          <Reveal className="mt-20">
            <Ornament tone="var(--c-gold)" className="mb-8" />
            <h3 className="h2-sub text-center">Browse by mukhi</h3>
            <p className="mx-auto mt-3 max-w-xl text-center text-[15px] leading-[1.75] text-dim">
              One through fourteen, with the deity and graha each is traditionally read with.
            </p>
          </Reveal>
          <div className="mt-10">
            <MukhiQuickNav includeSpecials={false} />
          </div>
        </Container>
      </Section>

      {/* 10 — The consultation block */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container className="grid items-start gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
          <Reveal>
            <Eyebrow>The Proof-First Session</Eyebrow>
            <H2 className="mt-5">
              Twenty minutes. <span className="num">₹499</span>. Credited back. Worth{" "}
              <span className="num">₹3,099</span> stacked.
            </H2>
            <div className="measure mt-7 space-y-5 text-base leading-[1.65] text-dim">
              <p>
                A named consultant looks at your chart and tells you what — if anything — is worth
                wearing, backed by a written analysis sent beforehand and a recording sent after. The
                ₹499 comes off any purchase over ₹5,000 within thirty days — so it's really free to
                anyone who buys, and ₹499 for twenty minutes of a real person's attention to everyone
                else.
              </p>
              <p>
                There is no meter. There is no wallet. And the person advising you earns nothing at all
                from what you decide to buy — no commission, no bonus, no target. If the honest answer
                is that you need nothing, that's what you'll be told.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <ButtonLink to="/consult/suitability-session" data-testid="home-book-proof-first">
                Get a straight answer — <span className="num">₹499</span>, credited back
              </ButtonLink>
              <Link to="/consult" className="link-u ui text-sm">See all consultations →</Link>
            </div>
            <GuaranteeLine testId="home-consult-guarantee" className="mt-4">
              Credited back in full against any purchase over ₹5,000 within 30 days.
            </GuaranteeLine>
          </Reveal>

          <Reveal delay={0.12} className="lg:sticky lg:top-28 lg:self-start">
            <div className="card-s elev p-8" data-testid="home-value-stack">
              <p className="eyebrow mb-5">What's in the stack</p>
              <table className="w-full text-sm">
                <tbody>
                  {(svc?.stack_items || []).map((s) => (
                    <tr key={s.label} className="border-b border-rule/70">
                      <td className="ui py-3.5 pr-4 leading-snug text-dim">{s.label}</td>
                      <td className="num py-3.5 text-right text-ink">{format(s.value_inr)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-rule">
                    <td className="ui py-3.5 pr-4 font-semibold text-ink">Total stack value</td>
                    <td className="num py-3.5 text-right font-semibold text-ink">
                      {svc ? format(svc.stack_value_total) : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="ui py-3.5 pr-4 font-semibold text-gold">You pay</td>
                    <td className="num py-3.5 text-right text-xl font-semibold text-gold">
                      {svc ? format(svc.price_inr) : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="ui mt-5 text-xs leading-[1.7] text-dim">
                Stack values are what each component would cost standalone. This is an additive total,
                not a struck-through "was" price — we do not display those anywhere.
              </p>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* 11 — The Twelve Promises, forced into the dark art direction */}
      <Section className={`${PAD} force-dark relative overflow-hidden border-y border-rule`}>
        <Container>
          <Reveal>
            <p className="eyebrow-wide !text-gold-bright">The Twelve Promises</p>
            <h2 className="section-title mt-5 max-w-3xl">
              Every business in this category says it is trustworthy. Very few will tell you how to
              check.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-4" stagger={0.09}>
            {PROMISE_TEASERS.map((p, i) => (
              <StaggerItem key={p} className="bg-[#141310] p-8">
                <Ordinal n={i + 1} className="!text-2xl !text-gold-bright/60" />
                <p className="mt-5 text-[15px] leading-[1.75] text-wash/90">{p}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.1}>
            <Link
              to="/promises"
              data-testid="home-all-promises"
              className="ui group mt-14 inline-flex items-center gap-2 border border-gold-bright px-7 py-3.5 text-sm text-gold-bright transition-colors duration-300 hover:bg-gold-bright hover:text-[#141310]"
            >
              Read all twelve promises
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </Container>
      </Section>

      {/* 12 — Free tools */}
      <Section className={`${PAD} border-b border-rule`}>
        <Container>
          <SectionHeader eyebrow="Free tools" title="Useful whether or not you buy anything" />
          <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" stagger={0.07}>
            <StaggerItem className="lg:col-span-2">
              <Link
                to="/tools/verify-a-certificate"
                data-testid="tool-tile-verify"
                className="group flex h-full flex-col justify-between border-2 border-verify bg-verify/[0.05] p-8 transition-colors duration-300 hover:bg-verify/[0.11] lg:p-10"
              >
                <div>
                  <p className="eyebrow !text-verify">Verify a certificate — even one you bought elsewhere</p>
                  <H3 className="mt-4 h2-sub">
                    Enter any report number from GIA, IGI, GTL Jaipur or GRS
                  </H3>
                  <p className="measure mt-4 text-[15px] leading-[1.75] text-dim">
                    We'll show you how to check it on the laboratory's own website. Yes, including
                    stones you didn't buy from us.
                  </p>
                </div>
                <span className="link-u ui mt-8 inline-flex items-center gap-1.5 self-start text-sm">
                  Open the tool
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </StaggerItem>
            {TOOLS_NAV.filter((t) => !t.to.includes("verify")).map((t) => (
              <StaggerItem key={t.to}>
                <Link
                  to={t.to}
                  data-testid={`tool-tile-${t.to.split("/").pop()}`}
                  className="card-s elev group flex h-full flex-col p-7 transition-colors duration-300 hover:border-gold"
                >
                  <H3 className="transition-colors group-hover:text-gold">{t.label}</H3>
                  <p className="mt-2.5 flex-1 text-[15px] leading-[1.75] text-dim">{t.note}</p>
                  <ArrowUpRight className="mt-5 h-4 w-4 text-dim transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>

      {/* 13 — Reviews: empty until real ones are collected */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container>
          <SectionHeader
            eyebrow="Reviews"
            title="From verified purchases only"
            lede="When there are reviews here they will come from verified purchases, we will not edit them, and we will never have paid for one."
          />
          <Testimonials items={[]} />
        </Container>
      </Section>

      {/* 14 — Scoreboard strip */}
      <Section className={`${PAD} border-b border-rule`}>
        <Container>
          <SectionHeader
            eyebrow="Our scoreboard"
            title="Live numbers, updated weekly"
            action={
              <Link to="/about/scoreboard" className="link-u ui text-sm" data-testid="home-scoreboard-link">
                See the full scoreboard →
              </Link>
            }
          />
          <ScoreboardStrip />
        </Container>
      </Section>

      {/* 15 — Festival & muhurat calendar */}
      <Section className={`${PAD} border-b border-rule bg-surface`}>
        <Container>
          <SectionHeader
            eyebrow="Festival & muhurat calendar"
            title="The next four dated entries"
            action={<Link to="/tools/muhurat-calendar" className="link-u ui text-sm">Full calendar →</Link>}
          />
          <Stagger className="grid gap-px bg-rule sm:grid-cols-2 lg:grid-cols-4" stagger={0.08}>
            {(festivals.data?.upcoming || []).slice(0, 4).map((f) => (
              <StaggerItem key={f.slug}>
                <Link
                  to={f.collection}
                  data-testid={`festival-${f.slug}`}
                  className="group flex h-full flex-col bg-surface p-8 transition-colors duration-300 hover:bg-wash"
                >
                  <p className="num text-xs text-gold">{f.date}</p>
                  <H3 className="mt-3 transition-colors group-hover:text-gold">{f.name}</H3>
                  <p className="num mt-2.5 text-xs text-dim">{f.muhurat}</p>
                  <p className="mt-4 flex-1 text-[15px] leading-[1.75] text-dim">{f.note}</p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Section>

      {/* 16 — WhatsApp community */}
      <Section className={PAD}>
        <Container className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Eyebrow>WhatsApp circle</Eyebrow>
            <H2 className="mt-5">Join the WhatsApp circle</H2>
            <Lede className="mt-5">
              Muhurat dates, festival reminders, new arrivals and honest gemstone guidance. About one
              message a week. Leave any time — the unsubscribe link is in every message.
            </Lede>
          </Reveal>
          <Reveal delay={0.12} className="lg:pt-16">
            <CommunityForm />
          </Reveal>
        </Container>
      </Section>

      {/* 17 — Final CTA */}
      <FinalCTA />
    </>
  );
}
